import React, { useEffect, useState } from 'react';
import { Box, FormControlLabel, Grid, Typography } from '@mui/material';
import {
  ContinueMessage,
  PageTransitionWrapper,
  RadioButton,
  SelectableWrapper,
} from '../../components';
import { NOTICE_PERIOD_LIST } from '../../constants/noticePeriodList';
import { useAppDispatch, useAppSelector } from '../../redux';
import { updateNoticePeriod, userSelectors } from '../../redux/user';
import {
  gamifiedResponseSelectors,
  gamifiedResponseSlice,
} from '../../redux/gamifiedResponse';
import { getUserPrefferedName } from '../../utils';
import GoToPreviousStep from '../../components/goToPreviousStep/GoToPreviousStep';
import { logEvent } from 'src/services';
import isNumber from 'lodash/isNumber';

export const NoticePeriod: React.FC = () => {
  const dispatch = useAppDispatch();
  const userData = useAppSelector(userSelectors.getUserData)!;
  const lastGamifiedMessage = useAppSelector(
    gamifiedResponseSelectors.getGamifiedResponse,
  );
  const [noticePeriodChecked, setNoticePeriodChecked] = useState<number | null>(
    userData.noticePeriod ?? null,
  );

  useEffect(() => {
    if (isNumber(userData.noticePeriod)) {
      dispatch(gamifiedResponseSlice.actions.updateMessage(null));
    } else {
      if (!lastGamifiedMessage) {
        dispatch(
          gamifiedResponseSlice.actions.updateMessage(
            `Welcome back, ${getUserPrefferedName(userData)}!`,
          ),
        );
      }
    }

    logEvent('launchpod-notice-period-page-loaded');
  }, []);

  const handleChange = (noticePeriodSelected: string) => {
    const noticePeriodNumber = +noticePeriodSelected;

    logEvent('launchpod-notice-period-selected');

    setNoticePeriodChecked(noticePeriodNumber);
    dispatch(
      gamifiedResponseSlice.actions.updateMessage('Hey, the stage is yours'),
    );
    setTimeout(() => {
      dispatch(updateNoticePeriod(noticePeriodNumber));
    }, 500);
  };

  const midOption = Math.round(NOTICE_PERIOD_LIST.length / 2) + 1;
  const firstOption = NOTICE_PERIOD_LIST[0];
  const leftColumn = NOTICE_PERIOD_LIST.slice(1, midOption);
  const rightColumn = NOTICE_PERIOD_LIST.slice(midOption);

  return (
    <PageTransitionWrapper>
      <Box display="flex" data-testid="notice-period-layout">
        <GoToPreviousStep />
        <Typography variant="h2">
          What would be your notice{' '}
          <Box
            component="br"
            sx={(theme) => ({
              [theme.breakpoints.between('md', 'lg')]: {
                display: 'none',
              },
              [theme.breakpoints.down('sm')]: {
                display: 'none',
              },
            })}
          />
          period after getting an offer?
        </Typography>
      </Box>
      <Grid
        direction="row"
        container
        spacing={1}
        maxWidth={{ lg: '33rem' }}
        mb={1}
      >
        <Grid item xs={12}>
          <SelectableWrapper
            value={firstOption.value}
            selectedValue={noticePeriodChecked}
            onClick={handleChange}
            testid="notice-period-option"
          >
            <FormControlLabel
              value={firstOption.value}
              control={<RadioButton tabIndex={-1} />}
              checked={firstOption.value === noticePeriodChecked}
              label={firstOption.label}
            />
          </SelectableWrapper>
        </Grid>
        {leftColumn.map((option, idx) => {
          const rightOption = rightColumn[idx];

          return (
            <>
              <Grid item key={option.value} xs={12} sm={6}>
                <SelectableWrapper
                  value={option.value}
                  selectedValue={noticePeriodChecked}
                  onClick={handleChange}
                  testid="notice-period-option"
                >
                  <FormControlLabel
                    value={option.value}
                    control={<RadioButton tabIndex={-1} />}
                    checked={option.value === noticePeriodChecked}
                    label={option.label}
                  />
                </SelectableWrapper>
              </Grid>
              {!!rightOption && (
                <Grid item key={rightOption.value} xs={12} sm={6}>
                  <SelectableWrapper
                    value={rightOption.value}
                    selectedValue={noticePeriodChecked}
                    onClick={handleChange}
                    testid="notice-period-option"
                  >
                    <FormControlLabel
                      value={rightOption.value}
                      control={<RadioButton tabIndex={-1} />}
                      checked={rightOption.value === noticePeriodChecked}
                      label={rightOption.label}
                    />
                  </SelectableWrapper>
                </Grid>
              )}
            </>
          );
        })}
      </Grid>
      <ContinueMessage textIdentifier={1} />
    </PageTransitionWrapper>
  );
};

export default NoticePeriod;
